import { DISPLAY_DESKTOPSUBMENU } from '@components/Header/Header.config'
import NavSubMenu from '@components/Header/NavbarSubMenu'
import routes from '@config/routes'
import { useTranslation } from '@contexts/translation'
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
  Theme
} from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { ReactNode } from 'react'

interface MenuItemProps {
  path?: string
  title: string
  icon?: ReactNode
  baseURLs?: string[]
  exactURLs?: string[]
  excludedURLs?: string[]
  endornment?: ReactNode
  handleDrawerMobileClose?: () => void
  idList?: number[]
  margin?: string
}

export const MenuItem = ({
  path = '',
  title,
  icon,
  baseURLs = [],
  exactURLs = [],
  excludedURLs = [],
  endornment,
  handleDrawerMobileClose = () => {},
  idList = [],
  margin = '0px'
}: MenuItemProps): JSX.Element => {
  const router = useRouter()
  const id = router.query.id || router.query.project_id

  let isSelected = false

  if (baseURLs?.length > 0)
    for (const url of baseURLs) {
      if (
        (url === routes.opportunities.root ||
          router.asPath === routes.opportunities.root) &&
        id &&
        idList?.includes(+(id as string))
      )
        isSelected = true
      else if (
        url === routes.project.root &&
        id &&
        idList?.includes(+(id as string))
      ) {
        isSelected = false
      } else if (router.asPath.includes(url)) isSelected = true
    }
  else if (exactURLs?.length > 0)
    for (const url of exactURLs) {
      if (router.asPath === url) isSelected = true
    }

  if (excludedURLs?.length > 0)
    for (const url of excludedURLs) {
      if (router.asPath.includes(url)) isSelected = false
    }

  const closeMobileMenuHandler = (): void => handleDrawerMobileClose()

  return (
    <Link href={path} legacyBehavior>
      <a>
        <ListItemButton
          onClick={() => {
            if (window.innerWidth < 1000) closeMobileMenuHandler()
          }}
          selected={isSelected}
          sx={{
            ml: '10px',
            mr: '20px',
            my: '2.5px',
            px: margin,
            py: '8px',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            '&:hover': { background: (t: Theme) => t.palette.secondary.light },
            '&.Mui-selected': {
              '&:hover': {
                background: (t: Theme) => t.palette.secondary.light
              },
              background: (t: Theme) => t.palette.secondary.contrastText,
              boxShadow: 'none'
            }
          }}
          data-testid="ListItemButton-1857c1e7-7727-4cbc-810c-6aecfd032021"
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {icon && (
              <ListItemIcon
                sx={{
                  minWidth: 'unset',
                  pl: '10px',
                  mr: '12px',
                  color: isSelected ? 'secondary.main' : 'text.primary'
                }}
              >
                {icon}
              </ListItemIcon>
            )}

            <Typography
              variant="subtitle2"
              color={'text.primary'}
              fontWeight={500}
              data-testid="Typography-ed4f56d2-12c9-4a84-b6f5-41cc69be7941"
            >
              {title}
            </Typography>
          </div>

          {endornment && (
            <ListItemIcon
              sx={{
                fontSize: '12.5px',
                minWidth: 'unset',
                color: 'text.primary'
              }}
            >
              {endornment}
            </ListItemIcon>
          )}
        </ListItemButton>
      </a>
    </Link>
  )
}

interface CollapsableMenuItemsProps extends MenuItemProps {
  section?: string | null
  expanded: boolean
  onExpand: (expanded: boolean) => void
}

export const CollapsableMenuItems = ({
  path = '',
  title,
  icon,
  exactURLs = [],
  baseURLs = [],
  excludedURLs = [],
  idList = [],
  endornment,
  handleDrawerMobileClose = () => {},
  section = null,
  expanded,
  onExpand
}: CollapsableMenuItemsProps): JSX.Element => {
  const router = useRouter()
  const { dictionary } = useTranslation()

  const id = router.query.id || router.query.project_id

  let isSelected = false

  if (baseURLs?.length > 0)
    for (const url of baseURLs) {
      if (
        url === routes.opportunities.root &&
        id &&
        idList?.includes(+(id as string))
      )
        isSelected = true
      else if (
        url === routes.project.root &&
        id &&
        idList?.includes(+(id as string))
      ) {
        isSelected = false
      } else if (router.asPath.includes(url)) isSelected = true
    }
  else if (exactURLs?.length > 0)
    for (const url of exactURLs) {
      if (router.asPath === url) isSelected = true
    }

  if (excludedURLs?.length > 0) {
    for (const url of excludedURLs) {
      if (router.asPath.includes(url)) isSelected = false
    }
  }

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <ListItemButton
          onClick={() => onExpand(!expanded)}
          selected={isSelected}
          sx={{
            pl: 0,
            py: '8px',
            '&.Mui-selected': {
              '&:hover': {
                background: (t: Theme) => t.palette.secondary.light
              },
              background: (t: Theme) => t.palette.secondary.light,
              boxShadow: (t: Theme) =>
                `inset -2px 0px 0px ${t.palette.secondary.main}`
            }
          }}
          data-testid="ListItemButton-7d85a678-4624-469c-b12b-e2bdb998617b"
        >
          {icon && (
            <ListItemIcon
              sx={{
                minWidth: 'unset',
                pl: '28px',
                mr: '12px',
                color: isSelected ? 'secondary.main' : 'text.primary'
              }}
            >
              {icon}
            </ListItemIcon>
          )}

          <Typography
            variant={'subtitle2'}
            color={isSelected ? 'secondary.main' : 'text.primary'}
            data-testid="Typography-0f3462f1-a1df-4f03-b8ae-b1056c7ead87"
          >
            {title}
          </Typography>

          {endornment && (
            <ListItemIcon
              sx={{
                minWidth: 'unset',
                pl: '40px',
                mr: '0',
                color: isSelected ? 'secondary.main' : 'text.primary'
              }}
            >
              {endornment}
            </ListItemIcon>
          )}
        </ListItemButton>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div">
            {NavSubMenu?.[section ? section : title]?.length > 0 &&
              NavSubMenu?.[section ? section : title]?.map(
                (item: any, i: number) => (
                  <MenuItem
                    key={i}
                    path={item.link}
                    title={dictionary[`${item.display_name}`]}
                    exactURLs={item.exactURL ? [item.exactURL] : []}
                    baseURLs={item.exactURL ? [] : [item.link]}
                    handleDrawerMobileClose={handleDrawerMobileClose}
                    idList={idList}
                    margin={'40px'}
                    data-testid="MenuItem-5134ec41-8954-4cb4-a98f-a46ea95d8db6"
                  />
                )
              )}
          </List>
        </Collapse>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', md: DISPLAY_DESKTOPSUBMENU ? 'block' : 'none' }
        }}
      >
        <ListItemButton
          selected={isSelected}
          onClick={() => onExpand(!expanded)}
          sx={{
            ml: '10px',
            mr: '20px',
            my: '2.5px',
            px: 0,
            py: '8px',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            '&:hover': { background: (t: Theme) => t.palette.secondary.light },
            '&.Mui-selected': {
              '&:hover': {
                background: (t: Theme) => t.palette.secondary.light
              },
              background: (t: Theme) => t.palette.secondary.contrastText,
              boxShadow: 'none'
            }
          }}
          data-testid="ListItemButton-5a904068-8e7a-4edd-a5e1-3bb2624d6444"
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {icon && (
              <ListItemIcon
                sx={{
                  minWidth: 'unset',
                  pl: '10px',
                  mr: '12px',
                  color: isSelected ? 'secondary.main' : 'text.primary'
                }}
              >
                {icon}
              </ListItemIcon>
            )}

            <Typography
              variant={'subtitle2'}
              color={'text.primary'}
              fontWeight={500}
              data-testid="Typography-af4dce1c-c796-4631-87ab-8055e71141b7"
            >
              {title}
            </Typography>
          </div>

          {endornment && (
            <ListItemIcon
              sx={{
                minWidth: 'unset',
                color: 'text.primary'
              }}
            >
              {endornment}
            </ListItemIcon>
          )}
        </ListItemButton>

        <Collapse id="basic-menu" in={expanded} timeout="auto" unmountOnExit>
          {NavSubMenu?.[section ? section : title]?.length > 0 &&
            NavSubMenu?.[section ? section : title]?.map(
              (item: any, i: number) => (
                <MenuItem
                  key={i}
                  path={item.link}
                  title={dictionary[`${item.display_name}`]}
                  exactURLs={item.exactURL ? [item.exactURL] : []}
                  baseURLs={item.exactURL ? [] : [item.link]}
                  handleDrawerMobileClose={handleDrawerMobileClose}
                  idList={idList}
                  margin={'40px'}
                  data-testid="MenuItem-ed170f78-1984-4cf4-9c49-d02d0ad58a34"
                />
              )
            )}
        </Collapse>
      </Box>

      {!DISPLAY_DESKTOPSUBMENU && (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <MenuItem
            path={path}
            title={title}
            icon={icon}
            exactURLs={exactURLs}
            baseURLs={baseURLs}
            excludedURLs={excludedURLs}
            endornment={endornment}
            handleDrawerMobileClose={handleDrawerMobileClose}
            idList={idList}
            data-testid="MenuItem-65eb5a07-1cb7-499a-8c47-4069086ec28a"
          />
        </Box>
      )}
    </>
  )
}
